import React from 'react';
import axios from 'axios';
import { baseUrl } from '../assets/Animations';
import { getRequestHeaders } from '../api';
import './dashboard-2024.css'
import { format, getDate, getMonth, sub, subMonths, parseISO, isAfter, parse as parseMonthString } from 'date-fns';
import { HorizontalLinkCard } from '../DanitComponents/Task Cards/HorizontalLinkCard';
import { getApplications, getNumberOfCancellations, getInvoices, getOnboardingCards, getTasks, getTimesheets } from './dashboard2024Functions';
import {APIProvider, AdvancedMarker, InfoWindow, Map as GoogleMap, Pin} from '@vis.gl/react-google-maps';
import { MdAccessibility, MdExpandMore, MdExpandLess } from 'react-icons/md';
import InquiryByInstrument from '../Dashboard/InquiryByInstrument/InquiryByInstrument';
import { convertSchoolAbbreviationToFullName } from '../Calendar/calendarFunctions';
import { displayVersionNumber } from '../utilityFunctions';
import TeacherCancellationLeaderboard from './TeacherCancellationLeaderBoard/TeacherCancellationLeaderBoard';
import AdminKpisAndTrendsChartJS from './AdminKPIs';
import LessonsPerMonthChart from './LessonsPerMonthChart/LessonsPerMonthChart';
import SchoolInquiryYoYChart from './SchoolInquiryYoYChart/SchoolInquiryYoYChart';
import InquiryYearLineChart from './InquiryYearLineChart/InquiryYearLineChart';

const normalizeId = (value) => (value == null ? '' : String(value).trim());
const normalizeName = (value) =>
  value == null
    ? ''
    : String(value)
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const timesheetMonthKey = (monthYear) => {
  if (!monthYear) return null;
  const raw = String(monthYear).trim();
  let parsed = parseMonthString(raw, 'MMMM/yyyy', new Date());
  if (parsed instanceof Date && !Number.isNaN(parsed.getTime())) {
    return format(parsed, 'yyyy-MM');
  }
  parsed = parseMonthString(raw.replace(/\s+/g, ''), 'MMMM/yyyy', new Date());
  if (parsed instanceof Date && !Number.isNaN(parsed.getTime())) {
    return format(parsed, 'yyyy-MM');
  }
  return null;
};

const ensureStudentEntry = (aliasMap, entries, rawId, rawName) => {
  const normId = normalizeId(rawId);
  const trimmedName = rawName == null ? '' : String(rawName).trim();
  const normName = normalizeName(trimmedName);
  const aliasKeys = [];
  if (normId) aliasKeys.push(`id::${normId}`);
  if (normName) aliasKeys.push(`name::${normName}`);
  if (!aliasKeys.length) aliasKeys.push(`anon::${entries.length + 1}`);

  let entry = null;
  for (const key of aliasKeys) {
    if (aliasMap.has(key)) {
      entry = aliasMap.get(key);
      break;
    }
  }

  if (!entry) {
    entry = {
      primaryKey: aliasKeys[0],
      ids: new Set(),
      names: new Set(),
      families: new Set(),
      schools: new Set(),
      teachers: new Set(),
      lessonDetails: [],
      originalNames: new Set(),
      studentId: normId || null,
      displayName: trimmedName || normId || 'Unknown student',
      expected: 4,
      actual: 0
    };
    entries.push(entry);
  }

  if (normId) {
    entry.ids.add(normId);
    aliasMap.set(`id::${normId}`, entry);
    if (!entry.studentId) entry.studentId = normId;
    if (!entry.primaryKey || entry.primaryKey.startsWith('anon::')) {
      entry.primaryKey = `id::${normId}`;
    }
  }
  if (normName) {
    entry.names.add(normName);
    aliasMap.set(`name::${normName}`, entry);
    if (!entry.primaryKey || entry.primaryKey.startsWith('anon::')) {
      entry.primaryKey = `name::${normName}`;
    }
  }
  if (trimmedName) entry.originalNames.add(trimmedName);
  aliasMap.set(entry.primaryKey, entry);
  return entry;
};

const inferTimesheetMonthDate = (timesheet) => {
  if (!timesheet) return null;
  const rawMonthYear = typeof timesheet.monthYear === 'string' ? timesheet.monthYear.trim() : '';
  if (rawMonthYear) {
    let key = timesheetMonthKey(rawMonthYear);
    if (!key && rawMonthYear.includes('/')) {
      key = timesheetMonthKey(rawMonthYear.replace(/\//g, ' '));
    }
    if (key) {
      const isoCandidate = parseISO(`${key}-01`);
      if (isoCandidate instanceof Date && !Number.isNaN(isoCandidate.getTime())) {
        return isoCandidate;
      }
    }
    const parsedDate = new Date(rawMonthYear);
    if (parsedDate instanceof Date && !Number.isNaN(parsedDate.getTime())) {
      return new Date(parsedDate.getFullYear(), parsedDate.getMonth(), 1);
    }
  }

  if (typeof timesheet.year === 'number') {
    const rawMonthNumber =
      typeof timesheet.monthNumber === 'number'
        ? timesheet.monthNumber
        : typeof timesheet.month === 'number'
          ? timesheet.month
          : null;
    let monthIndex = null;
    if (rawMonthNumber != null) {
      if (rawMonthNumber >= 0 && rawMonthNumber <= 11) {
        monthIndex = rawMonthNumber;
      } else if (rawMonthNumber >= 1 && rawMonthNumber <= 12) {
        monthIndex = rawMonthNumber - 1;
      }
    }
    const fallbackDate = new Date(timesheet.year, monthIndex ?? 0, 1);
    if (fallbackDate instanceof Date && !Number.isNaN(fallbackDate.getTime())) {
      return fallbackDate;
    }
  }

  return null;
};

const buildLessonsPerMonthSeries = (timesheets) => {
  if (!Array.isArray(timesheets) || !timesheets.length) return [];
  const monthMap = new Map();

  timesheets.forEach((sheet) => {
    const monthDate = inferTimesheetMonthDate(sheet);
    if (!monthDate) return;
    const key = format(monthDate, 'yyyy-MM');
    const lessonsCount = Array.isArray(sheet?.families)
      ? sheet.families.reduce((sum, family) => {
          const lessons = Array.isArray(family?.lessons) ? family.lessons.length : 0;
          return sum + lessons;
        }, 0)
      : 0;

    if (!monthMap.has(key)) {
      monthMap.set(key, { date: monthDate, count: 0 });
    }
    const entry = monthMap.get(key);
    entry.count += lessonsCount;
  });

  return Array.from(monthMap.entries())
    .map(([key, entry]) => ({
      key,
      label: format(entry.date, 'MMM yyyy'),
      count: entry.count,
      date: entry.date
    }))
    .sort((a, b) => a.date.getTime() - b.date.getTime());
};

const buildInquiryYoYSeriesBySchool = (families) => {
  if (!Array.isArray(families) || !families.length) return {};

  const MIN_YEAR = 2021;
  const EXCLUDED_SCHOOLS = new Set(['GLMA']);
  const schoolMap = new Map();

  families.forEach((family) => {
    const school = family?.school;
    const onboardDate = family?.onboardDate;
    if (!school || !onboardDate || EXCLUDED_SCHOOLS.has(school)) return;
    const date = new Date(onboardDate);
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return;
    const year = date.getFullYear();
    if (year < MIN_YEAR) return;
    const monthIndex = date.getMonth();
    if (!schoolMap.has(school)) {
      schoolMap.set(school, new Map());
    }
    const countsMap = schoolMap.get(school);
    const key = `${year}-${monthIndex}`;
    countsMap.set(key, (countsMap.get(key) || 0) + 1);
  });

  const result = {};
  schoolMap.forEach((countsMap, school) => {
    const yearsSet = new Set();
    countsMap.forEach((_, key) => {
      const [yearStr] = key.split('-');
      const yearNum = Number(yearStr);
      if (yearNum >= MIN_YEAR) yearsSet.add(yearNum);
    });
    const years = Array.from(yearsSet).sort((a, b) => a - b);
    if (!years.length) return;

    const groups = MONTH_LABELS.map((label, monthIdx) => {
      const entries = years.map((year) => ({
        year,
        count: countsMap.get(`${year}-${monthIdx}`) || 0
      }));
      return { label, monthIdx, entries };
    });

    const maxCount = groups.reduce(
      (max, group) => Math.max(max, ...group.entries.map((entry) => entry.count)),
      0
    );
    const total = Array.from(countsMap.values()).reduce((sum, count) => sum + count, 0);
    result[school] = { years, groups, maxCount, total };
  });

  return result;
};

const Dashboard2024 = ({ userData, setStudentModal2023 }) => {
    const [students, setStudents] = React.useState([]);
    const [formattedConversionData, setFormattedConversionData] = React.useState([]);
    const [totals, setTotals] = React.useState([]);
    const [enrollment, setEnrollment] = React.useState([]);
    const [onboardingCards, setOnboardingCards] = React.useState(0);
    const [cancellations, setCancellations] = React.useState(0);
    const [leaderBoardCancellations, setLeaderBoardCancellations] = React.useState([]);
    const [applications, setApplications] = React.useState(0);
    const [timesheets, setTimesheets] = React.useState(0);
    const [tasks, setTasks] = React.useState(0);
    const [unSubmittedInvoices, setUnSubmittedInvoices] = React.useState(0);
    const [month, setMonth] = React.useState('');
    const [year, setYear] = React.useState('');
    const [showMarkerName, setShowMarkerName] = React.useState(false)

    const [deleteTempFamilies, setDeleteTempFamilies] = React.useState([])
    const [deleteGeoLocations, setDeleteGeoLocations] = React.useState([])
    const [monthlyInstInquiries, setMonthlyInstInquiries] = React.useState({});
    const [leaderBoardTimesheets, setLeaderBoardTimesheets] = React.useState([]);
    const [allTimesheets, setAllTimesheets] = React.useState([]);
    const [lessonsPerMonthLoading, setLessonsPerMonthLoading] = React.useState(false);
    const [lessonsPerMonthError, setLessonsPerMonthError] = React.useState(null);
    const [selectedInquiryYoYSchool, setSelectedInquiryYoYSchool] = React.useState('');
    const [inquiryViewMode, setInquiryViewMode] = React.useState('tiles');
    const [inquiryTileFillMode, setInquiryTileFillMode] = React.useState('conversion');
    const [statusMovementSummary, setStatusMovementSummary] = React.useState(null);
    const [beingOnboardedSummary, setBeingOnboardedSummary] = React.useState(null);
    const [beingOnboardedAging, setBeingOnboardedAging] = React.useState([]);
    const [statusMovementLoading, setStatusMovementLoading] = React.useState(false);
    const [statusMovementError, setStatusMovementError] = React.useState(null);
    const [teacherDirectory, setTeacherDirectory] = React.useState([]);
    const [leaderboardLoading, setLeaderboardLoading] = React.useState(false);
    const [leaderboardLoaded, setLeaderboardLoaded] = React.useState(false);
    const [leaderboardError, setLeaderboardError] = React.useState(null);
    const [calendarTeacherId, setCalendarTeacherId] = React.useState('');
    const [calendarTeacherEmail, setCalendarTeacherEmail] = React.useState('');
    const [calendarStatus, setCalendarStatus] = React.useState(null);
    const [calendarStatusLoading, setCalendarStatusLoading] = React.useState(false);
    const [calendarStatusError, setCalendarStatusError] = React.useState('');
    const [calendarBanner, setCalendarBanner] = React.useState(null);
    const [calendarConnectLoading, setCalendarConnectLoading] = React.useState(false);
    const [calendarDisconnectLoading, setCalendarDisconnectLoading] = React.useState(false);
    const [availabilityResult, setAvailabilityResult] = React.useState(null);
    const [availabilityUnavailable, setAvailabilityUnavailable] = React.useState([]);
    const [availabilityLoading, setAvailabilityLoading] = React.useState(false);
    const [availabilityError, setAvailabilityError] = React.useState('');
    const [availabilityStart, setAvailabilityStart] = React.useState('');
    const [availabilityEnd, setAvailabilityEnd] = React.useState('');
    const [availabilityDuration, setAvailabilityDuration] = React.useState('');
    const [availabilityInterval, setAvailabilityInterval] = React.useState('');
    const [pushTeacherId, setPushTeacherId] = React.useState('');
    const [pushTitle, setPushTitle] = React.useState('');
    const [pushBody, setPushBody] = React.useState('');
    const [pushData, setPushData] = React.useState('');
    const [pushSending, setPushSending] = React.useState(false);
    const [pushResult, setPushResult] = React.useState(null);
    const [pushError, setPushError] = React.useState('');
    const leaderboardShouldShowLoading = leaderboardLoading || (!leaderboardLoaded && !leaderboardError);

    const apiBaseUrl = React.useMemo(() => {
      const envUrl = (process?.env?.REACT_APP_API_BASE_URL || process?.env?.NEXT_PUBLIC_API_BASE_URL || '').trim();
      if (envUrl) {
        return envUrl.replace(/\/$/, '');
      }
      if (typeof window !== 'undefined') {
        const currentOrigin = window.location.origin;
        if (currentOrigin.includes('localhost:3000')) {
          return 'http://localhost:8080';
        }
      }
      return (baseUrl || '').replace(/\/$/, '');
    }, [baseUrl]);

    React.useEffect(() => {
      if (typeof window === 'undefined') return;
      const storedTeacherId = window.localStorage.getItem('calendarTeacherId');
      const storedTeacherEmail = window.localStorage.getItem('calendarTeacherEmail');
      if (storedTeacherId) setCalendarTeacherId(storedTeacherId);
      if (storedTeacherEmail) setCalendarTeacherEmail(storedTeacherEmail);
    }, []);

    const formatAvailabilityTimestamp = (value) => {
      if (!value) return '—';
      const date = typeof value === 'number' ? new Date(value * 1000) : new Date(value);
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return '—';
      }
      return date.toLocaleString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    };

    const toUnixSeconds = (value) => {
      if (value == null) return null;
      if (typeof value === 'number') {
        return value > 1e12 ? Math.round(value / 1000) : value;
      }
      const date = value instanceof Date ? value : new Date(value);
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return null;
      }
      return Math.round(date.getTime() / 1000);
    };

    const normalizeSlot = (slot) => {
      if (!slot) return null;
      const startUnix = toUnixSeconds(
        slot.start ??
        slot.start_time ??
        slot.startTime ??
        slot.begin ??
        slot.startDate ??
        slot.start_date
      );
      const endUnix = toUnixSeconds(
        slot.end ??
        slot.end_time ??
        slot.endTime ??
        slot.finish ??
        slot.endDate ??
        slot.end_date
      );
      if (startUnix == null || endUnix == null || endUnix <= startUnix) return null;
      return {
        startUnix,
        endUnix,
        startISO: slot.startISO || slot.start || slot.start_time || null,
        endISO: slot.endISO || slot.end || slot.end_time || null,
        durationMinutes: slot.durationMinutes || Math.max(1, Math.round((endUnix - startUnix) / 60))
      };
    };

    const normalizeSlots = (rawSlots) => {
      if (!Array.isArray(rawSlots)) return [];
      return rawSlots
        .map((slot) => normalizeSlot(slot))
        .filter(Boolean)
        .sort((a, b) => a.startUnix - b.startUnix);
    };

    const computeUnavailableRanges = (slots, windowStart, windowEnd) => {
      if (windowStart == null || windowEnd == null || windowEnd <= windowStart) return [];
      const ranges = [];
      let cursor = windowStart;
      slots.forEach((slot) => {
        if (slot.startUnix > cursor) {
          ranges.push([cursor, slot.startUnix]);
        }
        if (slot.endUnix > cursor) {
          cursor = slot.endUnix;
        }
      });
      if (cursor < windowEnd) {
        ranges.push([cursor, windowEnd]);
      }
      return ranges;
    };

    const resolvedAvailabilityTeacherName = React.useMemo(() => {
      if (availabilityResult?.teacher) {
        const teacher = availabilityResult.teacher;
        return (
          teacher.name ||
          teacher.displayName ||
          teacher.email ||
          teacher.teacherEmail ||
          teacher._id ||
          teacher.id ||
          calendarTeacherId ||
          'Unknown teacher'
        );
      }
      return calendarTeacherId || 'Unknown teacher';
    }, [availabilityResult, calendarTeacherId]);

    const handleTeacherIdChange = (event) => {
      const { value } = event.target;
      setCalendarTeacherId(value);
      setCalendarStatusError('');
      setAvailabilityError('');
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('calendarTeacherId', value);
      }
    };

    const handleTeacherEmailChange = (event) => {
      const { value } = event.target;
      setCalendarTeacherEmail(value);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('calendarTeacherEmail', value);
      }
    };

    const calendarStatusLabel = React.useMemo(() => {
      if (!calendarTeacherId?.trim()) {
        return 'Enter a teacher ID to load status.';
      }
      if (calendarStatusLoading) {
        return 'Checking status…';
      }
      if (calendarStatus?.connected) {
        return `Connected as ${calendarStatus.email || calendarTeacherEmail || 'unknown email'}`;
      }
      return 'Not connected';
    }, [calendarTeacherId, calendarStatus, calendarStatusLoading, calendarTeacherEmail]);

    const fetchCalendarStatus = React.useCallback(
      async (teacherIdValue) => {
        if (!teacherIdValue) {
          setCalendarStatus(null);
          return;
        }
        const apiBase = apiBaseUrl || baseUrl;
        setCalendarStatusLoading(true);
        setCalendarStatusError('');
        try {
          const url = `${apiBase}/api/integrations/nylas/status/${teacherIdValue}`;
          console.log('[nylas status] requesting', url);
          const { data } = await axios.get(url, getRequestHeaders());
          console.log('[nylas status] response', data);
          setCalendarStatus(data);
        } catch (error) {
          console.error('[nylas status] error', error?.response?.data || error);
          setCalendarStatus(null);
          setCalendarStatusError(
            error?.response?.data?.error || error?.message || 'Unable to load calendar status.'
          );
        } finally {
          setCalendarStatusLoading(false);
        }
      },
      [apiBaseUrl]
    );

    React.useEffect(() => {
      if (!calendarTeacherId) {
        setCalendarStatus(null);
        return;
      }
      fetchCalendarStatus(calendarTeacherId);
    }, [calendarTeacherId, fetchCalendarStatus]);

    React.useEffect(() => {
      if (typeof window === 'undefined') return;
      const params = new URLSearchParams(window.location.search);
      const connected = params.get('connected');
      const error = params.get('error');
      if (connected === '1') {
        setCalendarBanner({ type: 'success', message: 'Calendar connected successfully.' });
        if (calendarTeacherId) fetchCalendarStatus(calendarTeacherId);
        params.delete('connected');
        params.delete('error');
        window.history.replaceState(
          {},
          '',
          `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`
        );
      } else if (error) {
        setCalendarBanner({
          type: 'error',
          message: decodeURIComponent(error)
        });
        params.delete('connected');
        params.delete('error');
        window.history.replaceState(
          {},
          '',
          `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`
        );
      }
    }, [calendarTeacherId, fetchCalendarStatus]);

    const handleConnectCalendar = async () => {
      if (!calendarTeacherId?.trim()) {
        setCalendarStatusError('Enter a teacher ID before connecting.');
        return;
      }
      setCalendarConnectLoading(true);
      setCalendarStatusError('');
      try {
        const params = new URLSearchParams({ teacherId: calendarTeacherId.trim() });
        if (calendarTeacherEmail?.trim()) {
          params.set('email', calendarTeacherEmail.trim());
        }
        const apiBase = apiBaseUrl || baseUrl;
        const connectUrl = `${apiBase}/api/integrations/nylas/connect?${params.toString()}`;
        console.log('[nylas connect] redirect →', connectUrl);
        window.location.href = connectUrl;
      } catch (error) {
        console.error('[nylas connect] failed', error?.response?.data || error);
        setCalendarStatusError(error?.message || 'Unable to start calendar connection.');
        setCalendarConnectLoading(false);
      }
    };

    const handleDisconnectCalendar = async () => {
      if (!calendarTeacherId?.trim()) {
        setCalendarStatusError('Enter a teacher ID before disconnecting.');
        return;
      }
      setCalendarDisconnectLoading(true);
      setCalendarStatusError('');
      try {
        const apiBase = apiBaseUrl || baseUrl;
        const response = await axios.post(
          `${apiBase}/api/integrations/nylas/disconnect`,
          { teacherId: calendarTeacherId.trim() },
          getRequestHeaders()
        );
        console.log('[nylas disconnect] response', response.data);
        setCalendarBanner({ type: 'success', message: 'Calendar disconnected.' });
        fetchCalendarStatus(calendarTeacherId.trim());
      } catch (error) {
        console.error('[nylas disconnect] failed', error?.response?.data || error);
        setCalendarStatusError(
          error?.response?.data?.error || error?.message || 'Unable to disconnect calendar.'
        );
      } finally {
        setCalendarDisconnectLoading(false);
      }
    };

    const defaultLeaderboardMonth = format(subMonths(new Date(), 1), 'yyyy-MM');
    const fetchLeaderboardData = React.useCallback(async () => {
      if (leaderboardLoading || leaderboardLoaded) return;
      setLeaderboardLoading(true);
      setLeaderboardError(null);
      try {
        const [timesheetsRes, cancellationsRes] = await Promise.all([
          axios.get(`${baseUrl}/getalltimesheetsendpoint`, getRequestHeaders()),
          axios.get(`${baseUrl}/cancellation`, getRequestHeaders())
        ]);
        setLeaderBoardTimesheets(Array.isArray(timesheetsRes.data) ? timesheetsRes.data : []);
        setLeaderBoardCancellations(Array.isArray(cancellationsRes.data) ? cancellationsRes.data : []);
        setLeaderboardLoaded(true);
      } catch (error) {
        console.error(error);
        setLeaderboardError(error?.message || 'Failed to load leaderboard data.');
      } finally {
        setLeaderboardLoading(false);
      }
    }, [baseUrl, leaderboardLoading, leaderboardLoaded, getRequestHeaders]);
    const inquirySchools = React.useMemo(
      () => Object.keys(monthlyInstInquiries).sort((a, b) => a.localeCompare(b)),
      [monthlyInstInquiries]
    );
    const [selectedInquirySchool, setSelectedInquirySchool] = React.useState('');

    React.useEffect(() => {
      if (!inquirySchools.length) {
        if (selectedInquirySchool) setSelectedInquirySchool('');
        return;
      }
      if (!selectedInquirySchool || !inquirySchools.includes(selectedInquirySchool)) {
        setSelectedInquirySchool(inquirySchools[0]);
      }
    }, [inquirySchools, selectedInquirySchool]);

    const inquiryMonthData = React.useMemo(() => {
      if (!selectedInquirySchool) return [];
      const schoolData = monthlyInstInquiries[selectedInquirySchool] || {};
      const entries = Object.entries(schoolData).map(([month, instruments]) => {
        const parsed = parseMonthString(month, 'MMM/yyyy', new Date());
        const sortKey = parsed instanceof Date && !Number.isNaN(parsed.getTime())
          ? parsed
          : new Date(month);
        return {
          month,
          instruments,
          sortKey
        };
      });
      return entries
        .sort((a, b) => (b.sortKey?.getTime?.() || 0) - (a.sortKey?.getTime?.() || 0))
        .map(({ month, instruments }) => ({
          month,
          instruments,
          total: Object.values(instruments || {}).reduce((sum, count) => sum + count, 0)
        }));
    }, [monthlyInstInquiries, selectedInquirySchool]);

    const lessonsPerMonthSeries = React.useMemo(
      () => buildLessonsPerMonthSeries(allTimesheets),
      [allTimesheets]
    );
    const inquiriesYoYBySchool = React.useMemo(
      () => buildInquiryYoYSeriesBySchool(students),
      [students]
    );
    const inquiryYoYSchools = React.useMemo(() => {
      const preferredOrder = ['MMA', 'LSMA', 'TMA', 'MOR', 'PSMA', 'TEM'];
      const orderMap = new Map(preferredOrder.map((school, idx) => [school, idx]));
      return Object.keys(inquiriesYoYBySchool)
        .sort((a, b) => {
          const idxA = orderMap.has(a) ? orderMap.get(a) : preferredOrder.length;
          const idxB = orderMap.has(b) ? orderMap.get(b) : preferredOrder.length;
          if (idxA !== idxB) return idxA - idxB;
          return a.localeCompare(b);
        });
    }, [inquiriesYoYBySchool]);
    React.useEffect(() => {
      if (!inquiryYoYSchools.length) {
        if (selectedInquiryYoYSchool) setSelectedInquiryYoYSchool('');
        return;
      }
      if (!selectedInquiryYoYSchool || !inquiryYoYSchools.includes(selectedInquiryYoYSchool)) {
        setSelectedInquiryYoYSchool(inquiryYoYSchools[0]);
      }
    }, [inquiryYoYSchools, selectedInquiryYoYSchool]);

    const teacherOptions = React.useMemo(() => {
      const sources = [
        ...(Array.isArray(userData?.Teachers) ? userData.Teachers : []),
        ...(Array.isArray(userData?.teachers) ? userData.teachers : []),
        ...(Array.isArray(userData?.TeacherList) ? userData.TeacherList : []),
        ...(Array.isArray(teacherDirectory) ? teacherDirectory : [])
      ];
      const byId = new Map();
      sources.forEach((teacher) => {
        if (!teacher) return;
        const id = normalizeId(
          teacher?.TeacherID ||
          teacher?.teacherId ||
          teacher?.id ||
          teacher?.teacherID ||
          teacher?._id
        );
        if (!id) return;
        const name =
          teacher?.Name ||
          teacher?.teacherName ||
          teacher?.displayName ||
          teacher?.fullName ||
          teacher?.name ||
          id;
        const rawSchool =
          teacher?.School ||
          teacher?.school ||
          teacher?.schoolAbbreviation ||
          teacher?.schoolAbbrev ||
          '';
        const schoolAbbrev = rawSchool ? String(rawSchool).trim().toUpperCase() : '';
        const schoolLabel = schoolAbbrev
          ? convertSchoolAbbreviationToFullName(schoolAbbrev)
          : (teacher?.schoolName || teacher?.school || '');
        if (!byId.has(id)) {
          byId.set(id, {
            id,
            name,
            schoolAbbrev,
            schoolLabel: schoolLabel || schoolAbbrev || 'Unknown school'
          });
        }
      });
      return Array.from(byId.values()).sort((a, b) => {
        const schoolCompare = a.schoolLabel.localeCompare(b.schoolLabel);
        if (schoolCompare !== 0) return schoolCompare;
        return a.name.localeCompare(b.name);
      });
    }, [teacherDirectory, userData?.Teachers, userData?.teachers, userData?.TeacherList]);

    React.useEffect(() => {
      if (!teacherOptions.length) return;
      const current = teacherOptions.find((option) => option.id === pushTeacherId);
      if (current) return;
      const joshCandidate = teacherOptions.find((option) => {
        const nameMatch = normalizeName(option.name) === 'josh graham';
        const schoolMatch =
          option.schoolAbbrev === 'PSMA' ||
          normalizeName(option.schoolLabel).includes('parkside');
        return nameMatch && schoolMatch;
      });
      if (joshCandidate) {
        setPushTeacherId(joshCandidate.id);
        return;
      }
      setPushTeacherId(teacherOptions[0].id);
    }, [teacherOptions, pushTeacherId]);

    const {
      rows: debugActiveStudents,
      duplicateCount: activeRosterDuplicateCount,
      unassignedCount: activeRosterUnassignedCount
    } = React.useMemo(() => {
      const rows = [];
      const nameCounts = new Map();
      const teacherNameMap = new Map();
      const teacherSources = [
        ...(Array.isArray(userData?.Teachers) ? userData.Teachers : []),
        ...(Array.isArray(userData?.teachers) ? userData.teachers : []),
        ...(Array.isArray(userData?.TeacherList) ? userData.TeacherList : []),
        ...(Array.isArray(teacherDirectory) ? teacherDirectory : []),
        ...(Array.isArray(leaderBoardTimesheets) ? leaderBoardTimesheets : [])
      ];

      teacherSources.forEach((teacher) => {
        if (!teacher) return;
        const id = normalizeId(
          teacher?.TeacherID ||
          teacher?.teacherId ||
          teacher?.id ||
          teacher?.teacherID
        );
        if (!id) return;
        const name =
          teacher?.Name ||
          teacher?.teacherName ||
          teacher?.displayName ||
          teacher?.teacher ||
          teacher?.fullName ||
          teacher?.name ||
          id;
        teacherNameMap.set(id, name);
      });

      const firstNameTokens = (name) => {
        const normalized = normalizeName(name);
        if (!normalized) return [];
        const firstSegment = normalized.split(' ')[0] || normalized;
        if (!firstSegment) return [];
        const variants = new Set();
        variants.add(firstSegment);
        firstSegment.split(/[-/]/g).forEach((piece) => {
          const trimmedPiece = piece.trim();
          if (trimmedPiece) {
            variants.add(trimmedPiece);
          }
        });
        const alphanumeric = firstSegment.replace(/[^a-z0-9]/g, '');
        if (alphanumeric) {
          variants.add(alphanumeric);
        }
        return Array.from(variants);
      };

      (students || []).forEach((family) => {
        if (!family) return;
        const familyStatus = (family?.familyStatus || '').toLowerCase();
        if (!familyStatus.startsWith('active')) return;
        const familyName = family?.familyName || family?.family;
        const school = family?.school;
        const familyKey = normalizeName(familyName) || 'unknown-family';
        const studentList = Array.isArray(family?.students) ? family.students : [];
        studentList.forEach((student) => {
          const statusStr = (student?.studentStatus || '').toLowerCase();
          if (!statusStr.startsWith('active')) return;
          const studentName = student?.studentName || student?.name || 'Unknown student';
          const normName = normalizeName(studentName);
          const firstNameParts = firstNameTokens(studentName);
          if (normName) {
            const key = `${familyKey}::${normName}`;
            nameCounts.set(key, (nameCounts.get(key) || 0) + 1);
          }
          firstNameParts.forEach((token) => {
            const key = `${familyKey}::fn::${token}`;
            nameCounts.set(key, (nameCounts.get(key) || 0) + 1);
          });
          const teacherId = normalizeId(student?.teachersId || student?.teacherId || student?.teacherID);
          const candidateName = student?.teacherName || student?.teacher || student?.teacherFullName || '';
          if (teacherId && candidateName) {
            teacherNameMap.set(teacherId, candidateName);
          }
          const normalizedCandidate = normalizeName(candidateName);
          const teacherName = teacherId && teacherNameMap.has(teacherId)
            ? teacherNameMap.get(teacherId)
            : (student?.teacherName || student?.teacher || teacherId || '—');
          const isUnassigned = !teacherId && !normalizedCandidate;

          rows.push({
            studentName,
            familyName: familyName || '—',
            familyId: family?.familyId || family?.familyID || family?.id || null,
            teacherName,
            school: school || '—',
            unassigned: isUnassigned
          });
        });
      });

      rows.sort((a, b) => {
        if (a.school !== b.school) return a.school.localeCompare(b.school);
        if (a.familyName !== b.familyName) return a.familyName.localeCompare(b.familyName);
        return a.studentName.localeCompare(b.studentName);
      });

      const duplicateGroups = new Map();
      const rowsWithDuplicates = rows.map((row) => {
        const normName = normalizeName(row.studentName);
        const firstNameParts = firstNameTokens(row.studentName);
        const familyKey = normalizeName(row.familyName) || 'unknown-family';
        const duplicateExact = normName && nameCounts.get(`${familyKey}::${normName}`) > 1;
        const duplicateFirst = firstNameParts.some((token) => nameCounts.get(`${familyKey}::fn::${token}`) > 1);
        const duplicate = duplicateExact || duplicateFirst;
        if (duplicate) {
          let duplicateKey = null;
          if (duplicateExact) {
            duplicateKey = `full::${familyKey}::${normName}`;
          } else if (duplicateFirst && firstNameParts.length) {
            duplicateKey = `fn::${familyKey}::${firstNameParts[0]}`;
          }
          if (duplicateKey) {
            duplicateGroups.set(duplicateKey, (duplicateGroups.get(duplicateKey) || 0) + 1);
          }
        }
        return { ...row, duplicate };
      });
      let duplicateCount = 0;
      duplicateGroups.forEach((count) => {
        if (count > 1) {
          duplicateCount += (count - 1);
        }
      });
      const unassignedCount = rowsWithDuplicates.reduce(
        (sum, row) => sum + (row.unassigned ? 1 : 0),
        0
      );
      return { rows: rowsWithDuplicates, duplicateCount, unassignedCount };
    }, [students, userData?.Teachers, userData?.teachers, userData?.TeacherList, teacherDirectory, leaderBoardTimesheets]);

    const handleRosterRowClick = React.useCallback(
      (familyId) => {
        if (!familyId || typeof setStudentModal2023 !== 'function') return;
        setStudentModal2023(familyId);
      },
      [setStudentModal2023]
    );
    const rosterRowsAreInteractive = typeof setStudentModal2023 === 'function';
    const selectedSchoolYoYSeries = selectedInquiryYoYSchool
      ? inquiriesYoYBySchool[selectedInquiryYoYSchool]
      : null;
    const onboardingAverageDays = React.useMemo(() => {
      if (!Array.isArray(beingOnboardedAging) || !beingOnboardedAging.length) return 0;
      const validRows = beingOnboardedAging.filter((row) => Number.isFinite(row?.daysInStatus));
      if (!validRows.length) return 0;
      const totalDays = validRows.reduce((sum, row) => sum + row.daysInStatus, 0);
      return Math.round(totalDays / validRows.length);
    }, [beingOnboardedAging]);
    const onboardingAgingBuckets = React.useMemo(() => {
      const buckets = [
        { label: '0-7 days', min: 0, max: 7, count: 0 },
        { label: '8-14 days', min: 8, max: 14, count: 0 },
        { label: '15-30 days', min: 15, max: 30, count: 0 },
        { label: '30+ days', min: 31, max: Infinity, count: 0 }
      ];
      beingOnboardedAging.forEach((row) => {
        const days = Number(row?.daysInStatus);
        if (!Number.isFinite(days)) return;
        const bucket = buckets.find((entry) => days >= entry.min && days <= entry.max);
        if (bucket) bucket.count += 1;
      });
      return buckets;
    }, [beingOnboardedAging]);
    const beingOnboardedStudentRows = React.useMemo(
      () => beingOnboardedAging.filter((row) => row?.entityType === 'student'),
      [beingOnboardedAging]
    );

    React.useEffect(() => {
      let isMounted = true;
        setLessonsPerMonthLoading(true);
        setLessonsPerMonthError(null);
        axios.get(`${baseUrl}/getalltimesheetsendpoint`, getRequestHeaders())
            .then(res => {
                if (!isMounted) return;
                const timesheetData = Array.isArray(res.data) ? res.data : [];
                setAllTimesheets(timesheetData);
            })
            .catch(err => {
                if (!isMounted) return;
                setLessonsPerMonthError(err?.message || 'Failed to load lessons data.');
            })
            .finally(() => {
                if (!isMounted) return;
                setLessonsPerMonthLoading(false);
            });
        axios.get(`${baseUrl}/school-management/`, getRequestHeaders())
            .then(res => {
                if (!isMounted) return;
                setStudents(res.data);
            })
            .catch(err => {
                console.log(err);
            });
        axios.get(`${baseUrl}/initialadminrequest`, getRequestHeaders())
            .then(res => {
                if (!isMounted) return;
                if (!Array.isArray(res.data)) {
                    setTeacherDirectory([]);
                } else {
                    setTeacherDirectory(res.data);
                }
            })
            .catch(err => console.log(err));
        axios.get(`${baseUrl}/dashboard/inquiry-summary`, getRequestHeaders())
            .then(res => {
                if (!isMounted) return;
                setFormattedConversionData(Array.isArray(res.data) ? res.data : []);
            })
            .catch(err => {
                console.log(err);
            });
        axios.get(`${baseUrl}/dashboard-summary`, getRequestHeaders())
        .then(res => {
          //console.log(res.data)
            if (!isMounted) return;
            setTotals(res.data);
            //setFormattedConversionData(res.data.formattedConversionData);
        })
        .catch(err => console.error(err));
        setStatusMovementLoading(true);
        setStatusMovementError(null);
        Promise.all([
          axios.get(`${baseUrl}/dashboard/status-movement-summary`, getRequestHeaders()),
          axios.get(`${baseUrl}/dashboard/being-onboarded-summary`, getRequestHeaders()),
          axios.get(`${baseUrl}/dashboard/being-onboarded-aging`, getRequestHeaders())
        ])
          .then(([movementRes, onboardingSummaryRes, onboardingAgingRes]) => {
            if (!isMounted) return;
            setStatusMovementSummary(movementRes?.data || null);
            setBeingOnboardedSummary(onboardingSummaryRes?.data || null);
            setBeingOnboardedAging(Array.isArray(onboardingAgingRes?.data) ? onboardingAgingRes.data : []);
          })
          .catch((err) => {
            if (!isMounted) return;
            console.error(err);
            setStatusMovementError(err?.message || 'Failed to load status movement data.');
          })
          .finally(() => {
            if (!isMounted) return;
            setStatusMovementLoading(false);
          });
        
      const now = new Date();
      const calls = [0, 1, 2].map(i => {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        return axios.get(`${baseUrl}/metrics/intro-lessons/month`, {
          ...getRequestHeaders(),
          params: { year: d.getFullYear(), month: d.getMonth() + 1 }
        });
      });

      Promise.all(calls)
        .then(results => {
          const data = results.map(r => r.data);
          console.log('Intro lessons by month:', data);
        })
        .catch(console.error);

        // axios.get(`${baseUrl}/dashboard-chart-summary`, getRequestHeaders())
        // .then(res => {
        //   console.log(res.data)
        // })
        // .catch(err => console.error(err));
        
    // axios.get(`${baseUrl}/calendar/2/2025`, getRequestHeaders())
    //     .then(res => {
    //       processEvents(res.data)
    //         //console.log(res.data)
    //     })
    //     .catch(err => console.error(err));

        initializeDateSettings()
        async function fetchData() {
          await getTasks(setTasks);
          await getOnboardingCards(setOnboardingCards);
          await getNumberOfCancellations(setCancellations);
        }
        if(isMounted) {
        fetchData();
        }
        deleteGetTeachersFamilies()

        return () => { isMounted = false };

    }, []);

    React.useEffect(() => {

  }, []);

    React.useEffect(() => {
      async function fetchData() {
        if (month && year) {
          await getTimesheets(month, year, setTimesheets);
          await getInvoices(month, year, setUnSubmittedInvoices);
          await getApplications(setApplications);
        }
      }
    
      fetchData();
    }, [month, year]);

    React.useEffect(() => {
      let myTotal = 0;
      totals.forEach(total => {
        myTotal = total.totalStudents + myTotal
      })
      setEnrollment(myTotal);
    }, [totals])

    const processEvents = (events) => {
      let totalMintuesTaught = 0;
      //get keys from events
      Object.keys(events).forEach((key) => {
        events[key].forEach((event) => {
          if (!event.isPendingOffer) {
           totalMintuesTaught = totalMintuesTaught + event.lessonLength
          }
        });
      });
      let hours = Math.floor(totalMintuesTaught / 60);
      let revenue = hours * 84.51
      let COGS = hours * 40.26
      let grossProfit = revenue - COGS
      //console.log(`Hours: ${hours}, Revenue: $${revenue.toFixed(2)}, COGS: $${COGS.toFixed(2)}, Gross Profit: $${grossProfit.toFixed(2)}`)
    }

    const initializeDateSettings = () => {
        if(getDate(new Date()) < 15) {
            setMonth(getMonth(new Date()) !== 0 ? format(sub(new Date(), {months: 1}), 'MMMM') : 'December')
            setYear(getMonth(new Date()) !== 0 ? format(new Date(), 'yyyy') : format(sub(new Date(), {years: 1}), 'yyyy'))
        } else {
            setMonth(format(new Date(), 'MMMM'))
            setYear(format(new Date(), 'yyyy'))
        }
    }

    //   const generateTotals = (students) => {
    //     const totals = { MMA: 0, LSMA: 0, TMA: 0, MOR: 0, PSMA: 0 };
    
    //     students.forEach(family => {
    //         if (family.familyStatus === "Active") {
    //             family.students.forEach(student => {
    //                 if (student.studentStatus === "Active") {
    //                     totals[family.school] = (totals[family.school] || 0) + 1;
    //                 }
    //             });
    //         }
    //     });
    
    //     setTotals(totals);
    // };

      const redirectTo = (path) => { window.open(path, "_self").focus();}

      //React.useEffect(() => console.log(deleteGeoLocations), [deleteGeoLocations])

      const deleteGetTeachersFamilies = () => {
        axios.get(`${baseUrl}/waitlist`, getRequestHeaders())
        //axios.get(`${baseUrl}/teachersfamilies/06eef65cd541a8`, getRequestHeaders())
        .then((res) => {
          let mmaStudents = []
          for(let i = 0; i < res.data.length; i++) {
            if(res.data[i].school === 'MMA') {
              mmaStudents.push(res.data[i])
            }
          }
          //console.log(res.data)
            setDeleteTempFamilies(res.data)
        })
        .catch((err) => console.log(err))
    }

    React.useEffect(() => {
        if(deleteTempFamilies.length > 0) {
            //checkAllAddresses()
        }
    }, [deleteTempFamilies])

      const checkAllAddresses = async () => {
        let allGeoLocations = []
        let ethan = await getEthanLatLong()
        if(ethan) {
            allGeoLocations.push(ethan)
        }
        for(let i = 0; i < deleteTempFamilies.length; i++) {
            let res = await getGeo(deleteTempFamilies[i])
            if(res) {
                allGeoLocations.push(res)
            }
        }
        setDeleteGeoLocations(allGeoLocations)
        //console.log(allGeoLocations)
    }

const tallyInquiriesBySchoolMonthInstrument = (students) => {
  const tally = {};
  const twelveMonthsAgo = subMonths(new Date(), 12);

  students.forEach((family) => {
    const { onboardDate, school, students: studentList } = family;
    if (!onboardDate || !school || !Array.isArray(studentList)) return;

    const onboardDateObj = new Date(onboardDate);
    if (!isAfter(onboardDateObj, twelveMonthsAgo)) return;

    const month = format(onboardDateObj, 'MMM/yyyy');

    if (!tally[school]) tally[school] = {};
    if (!tally[school][month]) tally[school][month] = {};

    studentList.forEach((student) => {
      const instruments = student.instrumentList || [];
      instruments.forEach((inst) => {
        const parentInstrument = inst.parentInstrument;
        if (!parentInstrument) return;

        if (!tally[school][month][parentInstrument]) {
          tally[school][month][parentInstrument] = 0;
        }

        tally[school][month][parentInstrument] += 1;
      });
    });
  });

  // Now sort instruments by count within each month per school
  Object.keys(tally).forEach((school) => {
    Object.keys(tally[school]).forEach((month) => {
      const unsorted = tally[school][month];
      const sortedEntries = Object.entries(unsorted).sort((a, b) => b[1] - a[1]);
      const sortedObject = Object.fromEntries(sortedEntries);
      tally[school][month] = sortedObject;
    });
  });

  setMonthlyInstInquiries(tally);
  return tally;
};


    const replaceSpacesWithPlus = (string) => {
        let newString = ''
        for(let i = 0; i < string.length; i++) {
            if(string[i] === ' ') {
                newString = newString + '+'
            } else {
                newString = newString + string[i]
            }
        }
        return newString
    }

      const getEthanLatLong = async () => {
          let myAddress = '3119+Westwood+Ct,+Boulder,+CO'
          return axios.get(`https://maps.googleapis.com/maps/api/geocode/json?address=${myAddress}&key=AIzaSyDKCEsq1kPacCY7j2S78YOskPLApGtK3jQ`)
          .then((res) => {
              if(res.data.status === 'OK') {
                  //create random id, name, and location
                  //add to deleteGeoLocations
                  let myId = Math.floor(Math.random() * (100000000 - 0 + 1) + 0)
                  let myName = 'Ethan'
                  let myLocation = res.data.results[0].geometry.location
                  let myObject = {id: myId, name: myName, location: myLocation, home: true}
                  return myObject
              }
          })
          .catch((err) => {
              console.log(err)
              return null
          })
      }

      const getGeo = async (fam) => {
        //console.log(fam)
        //replace spaces with + signs


        let myAddress = `${replaceSpacesWithPlus(fam.address.addressLine1)},+${fam.address.city},+${fam.address.state}`
        return axios.get(`https://maps.googleapis.com/maps/api/geocode/json?address=${myAddress}&key=AIzaSyDKCEsq1kPacCY7j2S78YOskPLApGtK3jQ`)
        .then((res) => {
            if(res.data.status === 'OK') {
                //create random id, name, and location
                //add to deleteGeoLocations
                let myId = Math.floor(Math.random() * (100000000 - 0 + 1) + 0)
                let myName = fam.familyName
                let myLocation = res.data.results[0].geometry.location
                let myObject = {id: myId, name: myName, students: fam.students.length, address: `${fam.address.addressLine1} ${fam.address.city}`, inst: createInstArray(fam), location: myLocation, home: false}
                return myObject
            }
        })
        .catch((err) => {
            console.log(err)
            return null
        })
    }

    const createInstArray = (fam) => {
        let instString = []
        for(let i = 0; i < fam.students.length; i++) {
            fam.students.forEach(student => {
              student.instrumentList.forEach(inst => {
                if(instString.includes(inst.name)) {
                  return
                } else {
                  instString.push(inst.name)
                }
              })
            })
        }
        //console.log(instString)
        return instString
    }

      const CustomMarkers = ({points}) => {
        return (
            <>
                {points.map((location, index) => {
                  //console.log(location)
                        return(
                            <AdvancedMarker onClick={() => setShowMarkerName(location.id)} key={location.id} position={location.location}>
                                {showMarkerName === location.id ? 
                                  <div className="test-marker" position={location.location}>
                                    <div className="test-marker__name">{location.name} Family</div>
                                    <div className="test-marker__address">{location.address}</div>
                                    <div className='test-marker__icon-row'>
                                      <div className="test-marker__students">
                                        <MdAccessibility />
                                        <div>{location.students}</div>
                                      </div>
                                      <div className='test-marker__instruments'>
                                        {location.inst.map((inst, index) => (
                                          <div key={index} className='test-marker__inst'>{inst}</div>
                                        ))}
                                      </div>
                                    </div>
                                    <div className='little-box'></div>
                                  </div>
                                :
                                <div className='test-pin'>
                                <Pin background={location.home ? 'blue' : 'red'} borderColor={location.home ? 'blue' : '#B31413'} glyphColor={location.home ? 'white' : '#B31413'} />
                                </div>
                }
                              
                                {/* <InfoWindow position={location.location}><p>{location.name}</p></InfoWindow>} */}
                            </AdvancedMarker>
                        )
                    })}
            </>
        )
    }

// Assumes axios and `baseUrl` are available in your module scope.
const testAvailabilityOnce = async () => {
  const apiBase = apiBaseUrl || baseUrl;
  const teacherId = (calendarTeacherId || '').trim();
  if (!teacherId) {
    setAvailabilityError('Enter a teacher ID to check availability.');
    return;
  }
  setAvailabilityLoading(true);
  setAvailabilityError('');

  const payload = { teacherId };
  if (availabilityStart) {
    payload.start = new Date(availabilityStart).toISOString();
  }
  if (availabilityEnd) {
    payload.end = new Date(availabilityEnd).toISOString();
  }
  if (availabilityDuration) {
    payload.durationMinutes = Number(availabilityDuration);
  }
  if (availabilityInterval) {
    payload.intervalMinutes = Number(availabilityInterval);
  }

  try {
    const res = await axios.post(
      `${apiBase}/api/integrations/nylas/availability`,
      payload,
      getRequestHeaders()
    );
    console.log('[nylas availability] response', res.data);
    const data = res.data || {};
    const normalizedSlots = normalizeSlots(data.timeSlots || data.time_slots || []);
    const windowStart =
      toUnixSeconds(
        data.window?.start ??
        data.window?.start_time ??
        data.window?.startTime ??
        data.window?.begin ??
        data.window?.startISO
      ) ??
      normalizedSlots[0]?.startUnix ??
      Math.round(Date.now() / 1000);
    const windowEnd =
      toUnixSeconds(
        data.window?.end ??
        data.window?.end_time ??
        data.window?.endTime ??
        data.window?.finish ??
        data.window?.endISO
      ) ??
      normalizedSlots[normalizedSlots.length - 1]?.endUnix ??
      windowStart + 7 * 24 * 60 * 60;
    const unavailable = computeUnavailableRanges(normalizedSlots, windowStart, windowEnd);

    setAvailabilityResult({
      teacher: data.teacher,
      requestId: data.requestId,
      window: {
        ...data.window,
        startUnix: windowStart,
        endUnix: windowEnd,
        startISO: data.window?.startISO || data.window?.start || data.window?.start_time || null,
        endISO: data.window?.endISO || data.window?.end || data.window?.end_time || null
      },
      slots: normalizedSlots,
      fetchedAt: new Date().toISOString(),
      durationMinutes: data.durationMinutes,
      intervalMinutes: data.intervalMinutes
    });
    setAvailabilityUnavailable(unavailable);
  } catch (err) {
    const status = err?.response?.status;
    const details = err?.response?.data;
    console.error("[teacher-availability test] error", status, details || err);
    setAvailabilityResult(null);
    setAvailabilityUnavailable([]);
    setAvailabilityError(details?.error || err.message || 'Unable to fetch teacher availability.');
    throw err;
  } finally {
    setAvailabilityLoading(false);
  }
};

    const handleSendPush = async () => {
      const teacherId = pushTeacherId?.trim();
      if (!teacherId) {
        setPushError('Select a teacher before sending.');
        return;
      }
      if (!pushBody.trim()) {
        setPushError('Add a message body before sending.');
        return;
      }
      setPushSending(true);
      setPushError('');
      setPushResult(null);
      let payloadData;
      if (pushData.trim()) {
        try {
          payloadData = JSON.parse(pushData);
        } catch (err) {
          setPushSending(false);
          setPushError('Custom data must be valid JSON.');
          return;
        }
      }
      try {
        const apiBase = apiBaseUrl || baseUrl;
        const payload = {
          teacherId,
          title: pushTitle.trim() || undefined,
          body: pushBody.trim(),
          data: payloadData
        };
        const res = await axios.post(`${apiBase}/push/send`, payload, getRequestHeaders());
        setPushResult(res.data);
      } catch (err) {
        const details = err?.response?.data;
        setPushError(details?.error || err?.message || 'Unable to send push.');
      } finally {
        setPushSending(false);
      }
    };

    return (
        <div>
            <h1>Admin Dashboard</h1>
            <div className='dashboard-2024-version-number'>{displayVersionNumber()}</div>
            {/* <div onClick={() => testAvailabilityOnce()}>click me</div> */}
            <div className='dashboard-2024__whole-dashboard'>

                <div className='dashboard-2024__main-section'>
                  {/* <div className='dashboard-2024__calendar-card'>
                    <div className='dashboard-2024__calendar-card-header'>
                      <div>
                        <div className='dashboard-2024__calendar-card-title'>Google Calendar Connection</div>
                        <p className='dashboard-2024__calendar-card-subtitle'>
                          Use your own teacher profile to verify the connect / disconnect flow before rolling it out to everyone.
                        </p>
                      </div>
                      <span
                        className={`dashboard-2024__calendar-status-pill ${
                          calendarStatus?.connected ? 'is-connected' : 'is-disconnected'
                        }`}
                      >
                        {calendarStatus?.connected ? 'Connected' : 'Not connected'}
                      </span>
                    </div>
                    {calendarBanner && (
                      <div
                        className={`dashboard-2024__calendar-banner dashboard-2024__calendar-banner--${calendarBanner.type}`}
                      >
                        {calendarBanner.message}
                      </div>
                    )}
                    <div className='dashboard-2024__calendar-inputs'>
                      <div className='dashboard-2024__calendar-input-group'>
                        <label htmlFor='calendar-teacher-id'>Teacher Mongo ID</label>
                        <input
                          id='calendar-teacher-id'
                          type='text'
                          className='dashboard-2024__calendar-input'
                          placeholder='64f5c0b4d9d8d1e123456789'
                          value={calendarTeacherId}
                          onChange={handleTeacherIdChange}
                        />
                      </div>
                      <div className='dashboard-2024__calendar-input-group'>
                        <label htmlFor='calendar-teacher-email'>Teacher Email (optional hint)</label>
                        <input
                          id='calendar-teacher-email'
                          type='email'
                          className='dashboard-2024__calendar-input'
                          placeholder='teacher@example.com'
                          value={calendarTeacherEmail}
                          onChange={handleTeacherEmailChange}
                        />
                        <span className='dashboard-2024__calendar-input-hint'>
                          Passed to the backend so Nylas can pre-fill Google login.
                        </span>
                      </div>
                    </div>
                    <p className='dashboard-2024__calendar-status-text'>{calendarStatusLabel}</p>
                    {calendarStatusError && (
                      <div className='dashboard-2024__calendar-error'>{calendarStatusError}</div>
                    )}
                    <div className='dashboard-2024__calendar-actions'>
                      <button
                        type='button'
                        className='dashboard-2024__calendar-button is-primary'
                        onClick={handleConnectCalendar}
                        disabled={
                          calendarConnectLoading ||
                          calendarStatusLoading ||
                          !calendarTeacherId?.trim()
                        }
                      >
                        {calendarConnectLoading ? 'Redirecting…' : 'Connect Google Calendar'}
                      </button>
                      {calendarStatus?.connected && (
                        <button
                          type='button'
                          className='dashboard-2024__calendar-button'
                          onClick={handleDisconnectCalendar}
                          disabled={calendarDisconnectLoading}
                        >
                          {calendarDisconnectLoading ? 'Disconnecting…' : 'Disconnect'}
                        </button>
                      )}
                      <button
                        type='button'
                        className='dashboard-2024__calendar-button is-outline'
                        onClick={() => calendarTeacherId?.trim() && fetchCalendarStatus(calendarTeacherId.trim())}
                        disabled={!calendarTeacherId?.trim() || calendarStatusLoading}
                      >
                        Refresh status
                      </button>
                    </div>
                  </div>
                  <div className='dashboard-2024__availability-card'>
                    <div className='dashboard-2024__availability-card-header'>
                      <div>
                        <div className='dashboard-2024__availability-card-title'>Teacher Availability Pilot</div>
                        <p className='dashboard-2024__availability-card-subtitle'>
                          Call <code>POST /api/integrations/nylas/availability</code> with the same teacher ID to preview the time slots teachers will see.
                        </p>
                      </div>
                      <button
                        type='button'
                        className='dashboard-2024__availability-button'
                        onClick={testAvailabilityOnce}
                        disabled={availabilityLoading || !calendarTeacherId?.trim()}
                      >
                        {availabilityLoading ? 'Checking…' : 'Check availability'}
                      </button>
                    </div>
                    <div className='dashboard-2024__availability-inputs'>
                      <div className='dashboard-2024__availability-input-group'>
                        <label htmlFor='availability-teacher-id'>Teacher Mongo ID</label>
                        <input
                          id='availability-teacher-id'
                          type='text'
                          className='dashboard-2024__availability-input'
                          value={calendarTeacherId}
                          onChange={handleTeacherIdChange}
                          placeholder='Matches the ID above'
                        />
                        <span className='dashboard-2024__availability-hint'>
                          Synced with the connection card so you can edit it in either place.
                        </span>
                      </div>
                      <div className='dashboard-2024__availability-grid'>
                        <div className='dashboard-2024__availability-input-group'>
                          <label htmlFor='availability-start'>Start (optional)</label>
                          <input
                            id='availability-start'
                            type='datetime-local'
                            className='dashboard-2024__availability-input'
                            value={availabilityStart}
                            onChange={(event) => setAvailabilityStart(event.target.value)}
                          />
                        </div>
                        <div className='dashboard-2024__availability-input-group'>
                          <label htmlFor='availability-end'>End (optional)</label>
                          <input
                            id='availability-end'
                            type='datetime-local'
                            className='dashboard-2024__availability-input'
                            value={availabilityEnd}
                            onChange={(event) => setAvailabilityEnd(event.target.value)}
                          />
                        </div>
                        <div className='dashboard-2024__availability-input-group'>
                          <label htmlFor='availability-duration'>Duration minutes</label>
                          <input
                            id='availability-duration'
                            type='number'
                            min='15'
                            step='5'
                            className='dashboard-2024__availability-input'
                            placeholder='Backend default'
                            value={availabilityDuration}
                            onChange={(event) => setAvailabilityDuration(event.target.value)}
                          />
                        </div>
                        <div className='dashboard-2024__availability-input-group'>
                          <label htmlFor='availability-interval'>Interval minutes</label>
                          <input
                            id='availability-interval'
                            type='number'
                            min='5'
                            step='5'
                            className='dashboard-2024__availability-input'
                            placeholder='Backend default'
                            value={availabilityInterval}
                            onChange={(event) => setAvailabilityInterval(event.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                    {availabilityLoading && (
                      <div className='dashboard-2024__availability-loading'>Checking availability…</div>
                    )}
                    {availabilityError && !availabilityLoading && (
                      <div className='dashboard-2024__availability-error'>{availabilityError}</div>
                    )}
                    {!availabilityResult && !availabilityLoading && !availabilityError && (
                      <p className='dashboard-2024__availability-placeholder'>
                        Run the check to capture availability and gaps for your selected window.
                      </p>
                    )}
                    {availabilityResult && (
                      <>
                        <div className='dashboard-2024__availability-meta'>
                          <div>
                            <div className='dashboard-2024__availability-meta-label'>Teacher</div>
                            <div className='dashboard-2024__availability-meta-value'>
                              {resolvedAvailabilityTeacherName}
                            </div>
                          </div>
                          <div>
                            <div className='dashboard-2024__availability-meta-label'>Window</div>
                            <div className='dashboard-2024__availability-meta-value'>
                              {formatAvailabilityTimestamp(
                                availabilityResult.window?.startISO || availabilityResult.window?.startUnix
                              )}
                              {' '}→{' '}
                              {formatAvailabilityTimestamp(
                                availabilityResult.window?.endISO || availabilityResult.window?.endUnix
                              )}
                            </div>
                          </div>
                          <div>
                            <div className='dashboard-2024__availability-meta-label'>Fetched</div>
                            <div className='dashboard-2024__availability-meta-value'>
                              {formatAvailabilityTimestamp(availabilityResult.fetchedAt)}
                            </div>
                          </div>
                          <div>
                            <div className='dashboard-2024__availability-meta-label'>Request ID</div>
                            <div className='dashboard-2024__availability-meta-value'>
                              {availabilityResult.requestId || '—'}
                            </div>
                          </div>
                          {(availabilityResult.durationMinutes || availabilityResult.intervalMinutes) && (
                            <div>
                              <div className='dashboard-2024__availability-meta-label'>Slot Settings</div>
                              <div className='dashboard-2024__availability-meta-value'>
                                {availabilityResult.durationMinutes ? `${availabilityResult.durationMinutes} min duration` : ''}
                                {availabilityResult.durationMinutes && availabilityResult.intervalMinutes ? ' · ' : ''}
                                {availabilityResult.intervalMinutes ? `${availabilityResult.intervalMinutes} min interval` : ''}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className='dashboard-2024__availability-columns'>
                          <div>
                            <div className='dashboard-2024__availability-column-title'>
                              Available Slots ({availabilityResult.slots.length})
                            </div>
                            {availabilityResult.slots.length ? (
                              <ul className='dashboard-2024__availability-slot-list'>
                                {availabilityResult.slots.map((slot, index) => (
                                  <li key={`${slot.startUnix}-${slot.endUnix}-${index}`}>
                                    <div>
                                      <div className='dashboard-2024__availability-slot-label'>
                                        {formatAvailabilityTimestamp(slot.startISO || slot.startUnix)}
                                      </div>
                                      <div className='dashboard-2024__availability-slot-sub'>
                                        to {formatAvailabilityTimestamp(slot.endISO || slot.endUnix)}
                                      </div>
                                    </div>
                                    <span className='dashboard-2024__availability-slot-duration'>
                                      {slot.durationMinutes} min
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className='dashboard-2024__availability-placeholder'>No slots returned.</p>
                            )}
                          </div>
                          <div>
                            <div className='dashboard-2024__availability-column-title'>
                              Unavailable Gaps ({availabilityUnavailable.length})
                            </div>
                            {availabilityUnavailable.length ? (
                              <ul className='dashboard-2024__availability-gap-list'>
                                {availabilityUnavailable.map(([start, end], index) => (
                                  <li key={`${start}-${end}-${index}`}>
                                    <div>
                                      <div className='dashboard-2024__availability-slot-label'>
                                        {formatAvailabilityTimestamp(start)}
                                      </div>
                                      <div className='dashboard-2024__availability-slot-sub'>
                                        to {formatAvailabilityTimestamp(end)}
                                      </div>
                                    </div>
                                    <span className='dashboard-2024__availability-slot-duration'>
                                      {Math.max(1, Math.round((end - start) / 60))} min
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className='dashboard-2024__availability-placeholder'>No gaps — fully covered.</p>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div> */}
                  {totals.length > 0 ?
                <div className="dashboard-2024__total-section">
                      <div className="dashboard-2024__totals-box">
                        <div className='dashboard-2024__total-label'>TOTAL ACTIVE</div>
                        <div className='dashboard-2024__total__amount'>{enrollment}</div>
                      </div>
                      {totals.map((total, index) => (
                        <div key={index} className="dashboard-2024__totals-box">
                          <div className='dashboard-2024__total-label'>{total.school}</div>
                          <div className='dashboard-2024__total__amount'>{total.totalStudents}</div>
                        </div>
                      ))}
                      {/* <div className="dashboard-2024__totals-box">
                          <div className='dashboard-2024__total-label'>MMA</div>
                          <div className='dashboard-2024__total__amount'>{totals["MMA"]}</div>
                      </div>
                      <div className="dashboard-2024__totals-box">
                          <div className='dashboard-2024__total-label'>LSMA</div>
                          <div className='dashboard-2024__total__amount'>{totals["LSMA"]}</div>
                      </div>
                      <div className="dashboard-2024__totals-box">
                          <div className='dashboard-2024__total-label'>TSMA</div>
                          <div className='dashboard-2024__total__amount'>{totals["TMA"]}</div>
                      </div>
                      <div className="dashboard-2024__totals-box">
                          <div className='dashboard-2024__total-label'>MOR</div>
                          <div className='dashboard-2024__total__amount'>{totals["MOR"]}</div>
                      </div>
                      <div className="dashboard-2024__totals-box">
                          <div className='dashboard-2024__total-label'>PSMA</div>
                          <div className='dashboard-2024__total__amount'>{totals["PSMA"]}</div>
                      </div> */}
                    </div>
                    :
                    <div className="dashboard-2024__total-section">
                    <div className="dashboard-2024__totals-box__with-flash">
                      <div className='dashboard-2024__total-label'>TOTAL ACTIVE</div>
                      <div className='dashboard-2024__total__amount'></div>
                    </div>
                    <div className="dashboard-2024__totals-box__with-flash">
                        <div className='dashboard-2024__total-label'>MMA</div>
                        <div className='dashboard-2024__total__amount'></div>
                    </div>
                    <div className="dashboard-2024__totals-box__with-flash">
                        <div className='dashboard-2024__total-label'>LSMA</div>
                        <div className='dashboard-2024__total__amount'></div>
                    </div>
                    <div className="dashboard-2024__totals-box__with-flash">
                        <div className='dashboard-2024__total-label'>TSMA</div>
                        <div className='dashboard-2024__total__amount'></div>
                    </div>
                    <div className="dashboard-2024__totals-box__with-flash">
                        <div className='dashboard-2024__total-label'>MOR</div>
                        <div className='dashboard-2024__total__amount'></div>
                    </div>
                    <div className="dashboard-2024__totals-box__with-flash">
                        <div className='dashboard-2024__total-label'>PSMA</div>
                        <div className='dashboard-2024__total__amount'></div>
                    </div>
                    <div className="dashboard-2024__totals-box__with-flash">
                        <div className='dashboard-2024__total-label'>TEM</div>
                        <div className='dashboard-2024__total__amount'></div>
                    </div>
                  </div>
                  }
                                      <AdminKpisAndTrendsChartJS middleContent={(
            <CollapsibleDashboardSection
              title="Inquiries"
              subtitle="Monthly inquiry volume, conversions, and conversion rate."
              showLabel="Show data"
              hideLabel="Hide data"
              defaultOpen
              headerExtras={(
                <>
                  <select
                    className='dashboard-2024__section-select'
                    value={inquiryViewMode}
                    onChange={(event) => setInquiryViewMode(event.target.value)}
                  >
                    <option value="tiles">Tile view</option>
                    <option value="line">Line chart</option>
                  </select>
                  {inquiryViewMode === 'tiles' && (
                    <select
                      className='dashboard-2024__section-select'
                      value={inquiryTileFillMode}
                      onChange={(event) => setInquiryTileFillMode(event.target.value)}
                    >
                      <option value="conversion">Fill: conversion rate</option>
                      <option value="inquiries">Fill: inquiries</option>
                    </select>
                  )}
                </>
              )}
            >
              {formattedConversionData.length > 0 ? (
                <div className="dashboard-2024__inquiries-section">
                  {inquiryViewMode === 'line' ? (
                    <InquiryYearLineChart data={formattedConversionData} />
                  ) : (
                    <div className="dashboard-2024__inquiries-grid">
                      {formattedConversionData.map((backUpInfo, index) => {
                        if (!backUpInfo.month) return null;
                        const conversionRate = Math.max(
                          0,
                          Math.min(100, Math.round(parseFloat(backUpInfo.conversionRate) || 0))
                        );
                        const inquiryCount = Math.max(
                          0,
                          Number(backUpInfo.numberOfStudentInquiries || 0)
                        );
                        const fillPercent = inquiryTileFillMode === 'inquiries'
                          ? Math.min(100, inquiryCount)
                          : conversionRate;

                        return (
                          <div
                            key={index}
                            className="dashboard-2024__inquiry-card"
                            style={{
                              '--inquiry-fill': fillPercent / 100,
                              '--inquiry-fill-alpha': Math.max(0.12, Math.min(0.65, fillPercent / 100))
                            }}
                          >
                            <div className="dashboard-2024__inquiry-card-header">
                              <div className="dashboard-2024__inquiry-month">{backUpInfo.month}</div>
                            </div>
                            <div className="dashboard-2024__inquiry-main-value">
                              {backUpInfo.numberOfStudentInquiries}
                            </div>
                            <div className="dashboard-2024__inquiry-rate-value">{conversionRate}%</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <div className='text-sm text-gray-500'>Loading inquiry metrics…</div>
              )}
            </CollapsibleDashboardSection>
                                      )} />

            <CollapsibleDashboardSection
              title="Lessons Taught Per Month"
              subtitle="Total lessons recorded across all submitted timesheets."
              showLabel="Show chart"
              hideLabel="Hide chart"
              defaultOpen
            >
              <LessonsPerMonthChart
                data={lessonsPerMonthSeries}
                loading={lessonsPerMonthLoading}
                error={lessonsPerMonthError}
              />
            </CollapsibleDashboardSection>
            <CollapsibleDashboardSection
              title="Status Movement"
              subtitle="Student activations, exits from Active, and current Being Onboarded aging."
              showLabel="Show data"
              hideLabel="Hide data"
              defaultOpen
            >
              {statusMovementLoading ? (
                <div className="text-sm text-slate-500">Loading status movement…</div>
              ) : statusMovementError ? (
                <div className="text-sm text-red-500">{statusMovementError}</div>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                    <StatusMetricCard
                      title="Activated This Week"
                      value={statusMovementSummary?.activatedThisWeek ?? 0}
                      tone="green"
                    />
                    <StatusMetricCard
                      title="Activated This Month"
                      value={statusMovementSummary?.activatedThisMonth ?? 0}
                      tone="green"
                    />
                    <StatusMetricCard
                      title="Left Active This Week"
                      value={statusMovementSummary?.leftActiveThisWeek ?? 0}
                      tone="red"
                    />
                    <StatusMetricCard
                      title="Left Active This Month"
                      value={statusMovementSummary?.leftActiveThisMonth ?? 0}
                      tone="red"
                    />
                    <StatusMetricCard
                      title="Families Being Onboarded"
                      value={beingOnboardedSummary?.familiesBeingOnboarded ?? 0}
                      tone="slate"
                    />
                    <StatusMetricCard
                      title="Students Being Onboarded"
                      value={beingOnboardedSummary?.studentsBeingOnboarded ?? 0}
                      tone="blue"
                      secondary={`Avg ${onboardingAverageDays} day${onboardingAverageDays === 1 ? '' : 's'} in status`}
                    />
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                      <div>
                        <div className="text-sm font-semibold text-slate-900">Being Onboarded Aging</div>
                        <div className="text-xs text-slate-500">Current entities grouped by time spent in status.</div>
                      </div>
                      <div className="text-xs text-slate-500">
                        {beingOnboardedAging.length} current item{beingOnboardedAging.length === 1 ? '' : 's'}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {onboardingAgingBuckets.map((bucket) => (
                        <div key={bucket.label} className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{bucket.label}</div>
                          <div className="mt-2 text-2xl font-semibold text-slate-900">{bucket.count}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-900">Current Being Onboarded</div>
                        <div className="text-xs text-slate-500">Students only, sorted by longest time in status.</div>
                      </div>
                    </div>
                    {beingOnboardedStudentRows.length ? (
                      <table className="min-w-full divide-y divide-slate-200 text-sm">
                        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                          <tr>
                            <th className="px-4 py-2 text-left">Student</th>
                            <th className="px-4 py-2 text-left">Family</th>
                            <th className="px-4 py-2 text-left">School</th>
                            <th className="px-4 py-2 text-left">Days</th>
                            <th className="px-4 py-2 text-left">Since</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 text-slate-700">
                          {beingOnboardedStudentRows.map((row, index) => (
                            <tr key={`${row.entityType}-${row.familyId}-${row.studentId || index}`} className="odd:bg-slate-50/40">
                              <td className="px-4 py-2">{row.studentName || '—'}</td>
                              <td className="px-4 py-2">{row.familyName || '—'}</td>
                              <td className="px-4 py-2">{row.school || '—'}</td>
                              <td className="px-4 py-2 font-medium text-slate-900">{Number.isFinite(row.daysInStatus) ? row.daysInStatus : '—'}</td>
                              <td className="px-4 py-2">
                                {row.effectiveDate ? format(new Date(row.effectiveDate), 'MMM d, yyyy') : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="px-4 py-6 text-sm text-slate-500">Nobody is currently in Being Onboarded.</div>
                    )}
                  </div>
                </div>
              )}
            </CollapsibleDashboardSection>
            <CollapsibleDashboardSection
              title="Inquiries by School (Year over Year)"
              subtitle="Compare school-level inquiries for each month across years."
              showLabel="Show chart"
              hideLabel="Hide chart"
            >
              {inquiryYoYSchools.length ? (
                <>
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      School
                    </label>
                    <select
                      className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      value={selectedInquiryYoYSchool}
                      onChange={(e) => setSelectedInquiryYoYSchool(e.target.value)}
                    >
                      {inquiryYoYSchools.map((school) => (
                        <option key={school} value={school}>
                          {school}
                        </option>
                      ))}
                    </select>
                  </div>
                  <SchoolInquiryYoYChart series={selectedSchoolYoYSeries} />
                </>
              ) : (
                <div className="text-sm text-gray-500">Need more inquiry data to show this chart.</div>
              )}
            </CollapsibleDashboardSection>
            <CollapsibleDashboardSection
              title="Teacher Performance Leaderboard"
              subtitle="Review taught lessons, active students, and cancellations by teacher."
              showLabel="Show leaderboard"
              hideLabel="Hide leaderboard"
              onOpenChange={(isOpen) => {
                if (isOpen) fetchLeaderboardData();
              }}
            >
              {leaderboardError && (
                <div className="mb-3 text-sm text-red-500">{leaderboardError}</div>
              )}
              <TeacherCancellationLeaderboard 
                cancellations={leaderBoardCancellations}
                defaultMonth={defaultLeaderboardMonth}
                timesheets={leaderBoardTimesheets}
                studentFamilies={students}
                loadingTimesheets={leaderboardShouldShowLoading} 
                loadingStudentFamilies={students.length === 0 ? true : false}
                loadingCancellations={leaderboardShouldShowLoading}
              />
            </CollapsibleDashboardSection>

            <CollapsibleDashboardSection
              title={`Active Student Roster (debug • duplicates: ${activeRosterDuplicateCount} • unassigned: ${activeRosterUnassignedCount})`}
              subtitle={`Current active roster snapshot (${debugActiveStudents.length} students)`}
              showLabel="Show roster"
              hideLabel="Hide roster"
            >
              {debugActiveStudents.length ? (
                <div className='overflow-x-auto max-h-[32rem] rounded-lg border border-slate-200 bg-white shadow-sm'>
                  <div className='flex flex-wrap items-center gap-4 px-4 py-3 border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500'>
                    <span>Duplicates: <span className='text-slate-900 normal-case text-sm font-medium'>{activeRosterDuplicateCount}</span></span>
                    <span>Unassigned: <span className='text-slate-900 normal-case text-sm font-medium'>{activeRosterUnassignedCount}</span></span>
                  </div>
                  <table className='min-w-full divide-y divide-slate-200 text-sm'>
                    <thead className='bg-slate-50 text-xs uppercase tracking-wide text-slate-500'>
                      <tr>
                        <th className='px-3 py-2 text-left'>#</th>
                        <th className='px-3 py-2 text-left'>Student</th>
                        <th className='px-3 py-2 text-left'>Family</th>
                        <th className='px-3 py-2 text-left'>Teacher</th>
                        <th className='px-3 py-2 text-left'>School</th>
                      </tr>
                    </thead>
                    <tbody className='divide-y divide-slate-100 text-slate-700'>
                      {debugActiveStudents.map((row, idx) => (
                        <tr
                          key={`${row.familyName}-${row.studentName}-${row.familyId || idx}`}
                          className={`odd:bg-slate-50/40 ${rosterRowsAreInteractive ? 'cursor-pointer hover:bg-indigo-50 focus-visible:bg-indigo-100 transition-colors' : ''}`}
                          onClick={() => {
                            if (rosterRowsAreInteractive) {
                              handleRosterRowClick(row.familyId);
                            }
                          }}
                          onKeyDown={(event) => {
                            if (!rosterRowsAreInteractive) return;
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handleRosterRowClick(row.familyId);
                            }
                          }}
                          tabIndex={rosterRowsAreInteractive ? 0 : undefined}
                          role={rosterRowsAreInteractive ? 'button' : undefined}
                        >
                          <td className='px-3 py-2 text-slate-400'>{idx + 1}</td>
                          <td className={`px-3 py-2 font-medium ${(row.duplicate || row.unassigned) ? 'text-rose-600' : 'text-slate-900'}`}>{row.studentName}</td>
                          <td className='px-3 py-2'>{row.familyName}</td>
                          <td className={`px-3 py-2 ${row.unassigned ? 'text-rose-600 font-semibold' : ''}`}>{row.teacherName}</td>
                          <td className='px-3 py-2 uppercase tracking-wide text-xs text-slate-500'>{row.school}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className='text-sm text-gray-500'>Active roster data is still loading.</div>
              )}
            </CollapsibleDashboardSection>

            <CollapsibleDashboardSection
              title="Inquiries by School (Last 12 Months)"
              subtitle="Instrument interest for each campus during the past year."
              showLabel="Show breakdown"
              hideLabel="Hide breakdown"
            >
              {inquirySchools.length ? (
                <>
                  <div className='flex flex-wrap items-center justify-between gap-3 mb-4'>
                    <p className='text-sm text-gray-600'>Switch schools to explore instrument demand.</p>
                    <div className='flex items-center gap-2'>
                      <label className='text-xs font-semibold tracking-wide text-gray-500 uppercase'>School</label>
                      <select
                        className='border border-gray-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'
                        value={selectedInquirySchool}
                        onChange={(e) => setSelectedInquirySchool(e.target.value)}
                      >
                        {inquirySchools.map((school) => (
                          <option key={school} value={school}>
                            {convertSchoolAbbreviationToFullName(school)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {inquiryMonthData.length ? (
                    <div className='grid gap-4 md:grid-cols-2 xl:grid-cols-3'>
                      {inquiryMonthData.map(({ month, instruments, total }) => (
                        <div
                          key={`${selectedInquirySchool}-${month}`}
                          className='rounded-lg border border-gray-200 bg-white shadow-sm p-4'
                        >
                          <div className='flex items-center justify-between mb-3'>
                            <span className='text-sm font-semibold text-gray-700'>{month}</span>
                            <span className='text-xs font-medium uppercase text-gray-400'>Total {total}</span>
                          </div>
                          <ul className='space-y-1 text-sm text-gray-600'>
                            {Object.entries(instruments).map(([instrument, count]) => (
                              <li key={instrument} className='flex items-center justify-between'>
                                <span>{instrument}</span>
                                <span className='font-semibold text-gray-900'>{count}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className='text-sm text-gray-500'>No inquiries recorded for the selected school.</div>
                  )}
                </>
              ) : (
                <div className='text-sm text-gray-500'>Inquiry data is loading…</div>
              )}
            </CollapsibleDashboardSection>
              {/* <div className='dashboard-2024__inquiry-box__with-flash'>
                <div className='dashboard-2024__inquiry-box__label'>UNDER CONSTRUCTION</div>
              </div> */}
              {/* <div className='dashboard-2024__inquiry-box__with-flash'>
                <div className='dashboard-2024__inquiry-box__label'>UNDER CONSTRUCTION</div>
              </div> */}
            {/* <div style={{width: '1200px', height: '600px'}}>
                <APIProvider apiKey={'AIzaSyDKCEsq1kPacCY7j2S78YOskPLApGtK3jQ'}>
                    <GoogleMap       
                        zoom={12}
                        center={{lat: 40.0150, lng: -105.2705}}
                        gestureHandling={'greedy'}
                        disableDefaultUI={true}
                        mapId={'3324feda48bc0f19'}
                    >
                        <CustomMarkers points={deleteGeoLocations} />
                    </GoogleMap>
                </APIProvider>
            </div> */}

      </div>

      <div className='dashboard-2024__right-column'>
        <div className='dashboard-2024__right-column__title'>NOTIFICATIONS</div>
        {onboardingCards ? <HorizontalLinkCard name={'Onboarding Cards'} action={() => redirectTo('/admin/students/adminonboarding3')} badge={{text: onboardingCards, color: {background: 'bg-blue-100', text: 'text-blue-500'}}} /> : null}
        {cancellations ? <HorizontalLinkCard name={'Cancellations'} action={() => redirectTo('/admin/teachers/cancellations')} badge={{text: cancellations, color: {background: 'bg-red-100', text: 'text-red-500'}}} /> : null}
        {applications ? <HorizontalLinkCard name={'Applications'} action={() => redirectTo('/admin/teachers/adminapplications')} badge={{text: applications, color: {background: 'bg-orange-100', text: 'text-orange-500'}}} /> : null}
        {timesheets ? <HorizontalLinkCard name={'Timesheets'} action={() => redirectTo('/admin/billing/timesheets')} badge={{text: timesheets, color: {background: 'bg-blue-100', text: 'text-blue-500'}}} /> : null}
        {unSubmittedInvoices ? <HorizontalLinkCard name={'Invoices'} action={() => redirectTo('/admin/billing/timesheets')} badge={{text: unSubmittedInvoices, color: {background: 'bg-blue-100', text: 'text-blue-500'}}} /> : null}
        {tasks ? <HorizontalLinkCard name={'Task Do Today'} action={() => redirectTo('/admin/tasks')} badge={{text: tasks, color: {background: 'bg-blue-100', text: 'text-blue-500'}}} /> : null}
      </div>
        </div>
        <div className='dashboard-2024__push-section'>
          <div className='dashboard-2024__push-header'>
            <div>
              <div className='dashboard-2024__push-title'>Teacher Push Notification</div>
              <div className='dashboard-2024__push-subtitle'>
                Send a one-off push notification to a teacher device.
              </div>
            </div>
            <button
              type='button'
              className='dashboard-2024__push-button'
              onClick={handleSendPush}
              disabled={pushSending || !pushTeacherId || !pushBody.trim()}
            >
              {pushSending ? 'Sending…' : 'Send push'}
            </button>
          </div>
          <div className='dashboard-2024__push-grid'>
            <div className='dashboard-2024__push-field'>
              <label htmlFor='push-teacher-select'>Teacher</label>
              <select
                id='push-teacher-select'
                className='dashboard-2024__push-input'
                value={pushTeacherId}
                onChange={(event) => setPushTeacherId(event.target.value)}
              >
                {teacherOptions.length ? (
                  teacherOptions.map((teacher) => (
                    <option key={teacher.id} value={teacher.id}>
                      {teacher.name} • {teacher.schoolLabel}
                    </option>
                  ))
                ) : (
                  <option value=''>Loading teachers…</option>
                )}
              </select>
            </div>
            <div className='dashboard-2024__push-field'>
              <label htmlFor='push-title'>Title (optional)</label>
              <input
                id='push-title'
                type='text'
                className='dashboard-2024__push-input'
                placeholder='Lesson reminder'
                value={pushTitle}
                onChange={(event) => setPushTitle(event.target.value)}
              />
            </div>
          </div>
          <div className='dashboard-2024__push-field'>
            <label htmlFor='push-body'>Message</label>
            <textarea
              id='push-body'
              className='dashboard-2024__push-textarea'
              placeholder='Write the push notification body...'
              value={pushBody}
              onChange={(event) => setPushBody(event.target.value)}
              rows={3}
            />
          </div>
          <div className='dashboard-2024__push-field'>
            <label htmlFor='push-data'>Custom data (JSON, optional)</label>
            <textarea
              id='push-data'
              className='dashboard-2024__push-textarea is-small'
              placeholder='{"route":"timesheets","source":"admin-dashboard"}'
              value={pushData}
              onChange={(event) => setPushData(event.target.value)}
              rows={2}
            />
          </div>
          {pushError && <div className='dashboard-2024__push-error'>{pushError}</div>}
          {pushResult && (
            <div className='dashboard-2024__push-result'>
              {pushResult?.sent != null
                ? `Sent to ${pushResult.sent} device${pushResult.sent === 1 ? '' : 's'}.`
                : 'Push request completed.'}
            </div>
          )}
        </div>
        </div>
    );
}

function CollapsibleDashboardSection({
  title,
  subtitle,
  children,
  defaultOpen = false,
  showLabel = "Show",
  hideLabel = "Hide",
  onOpenChange,
  headerExtras = null
}) {
  const [open, setOpen] = React.useState(defaultOpen);

  React.useEffect(() => {
    if (defaultOpen && typeof onOpenChange === "function") {
      onOpenChange(true);
    }
  }, [defaultOpen, onOpenChange]);

  const handleToggle = React.useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      if (typeof onOpenChange === "function") onOpenChange(next);
      return next;
    });
  }, [onOpenChange]);

  return (
    <div className='dashboard-2024__collapsible'>
      <div className='dashboard-2024__collapsible-header'>
        <div>
          <h3 className='dashboard-2024__collapsible-title'>{title}</h3>
          {subtitle && <p className='dashboard-2024__collapsible-subtitle'>{subtitle}</p>}
        </div>
        <div className='dashboard-2024__collapsible-actions'>
          {headerExtras}
          <button
            type="button"
            onClick={handleToggle}
            className='dashboard-2024__collapsible-toggle'
          >
            {open ? (
              <>
                <MdExpandLess className='h-4 w-4' />
                <span>{hideLabel}</span>
              </>
            ) : (
              <>
                <MdExpandMore className='h-4 w-4' />
                <span>{showLabel}</span>
              </>
            )}
          </button>
        </div>
      </div>
      {open && <div className='dashboard-2024__collapsible-body'>{children}</div>}
    </div>
  );
}

function StatusMetricCard({ title, value, tone = 'slate', secondary = '' }) {
  const toneClasses = {
    green: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    red: 'bg-rose-50 border-rose-200 text-rose-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    slate: 'bg-slate-50 border-slate-200 text-slate-700'
  };
  const valueToneClasses = {
    green: 'text-emerald-900',
    red: 'text-rose-900',
    blue: 'text-blue-900',
    slate: 'text-slate-900'
  };

  return (
    <div className={`rounded-2xl border p-4 shadow-sm ${toneClasses[tone] || toneClasses.slate}`}>
      <div className="text-xs font-semibold uppercase tracking-wide">{title}</div>
      <div className={`mt-3 text-3xl font-semibold ${valueToneClasses[tone] || valueToneClasses.slate}`}>
        {Number(value || 0).toLocaleString()}
      </div>
      {secondary ? (
        <div className="mt-2 text-xs text-slate-500">{secondary}</div>
      ) : null}
    </div>
  );
}

export default Dashboard2024;
